import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Subscribe to Supabase Realtime changes on a table and auto-invalidate TanStack queries.
 *
 * @param table - Supabase table name
 * @param queryKeys - TanStack query key prefixes to invalidate on any change
 * @param filterColumn - Column to filter on (usually "user_id")
 */
export function useRealtimeTable(
  table: string,
  queryKeys: string[],
  filterColumn = "user_id"
) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const channelId = `realtime-${table}-${queryKeys.join(",")}`;
    const channel = supabase
      .channel(channelId)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table,
          filter: `${filterColumn}=eq.${user.id}`,
        },
        () => {
          for (const key of queryKeys) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, table, queryClient, queryKeys, filterColumn]);
}
